import * as ActionTypes from "../actions/types";
import { NEW_BASE_URL } from "../config";
import { generalHeader } from "./headers";
import xs from "xstream/index";
import * as actions from "../actions";
import sampleCombine from "xstream/extra/sampleCombine";
import v4 from "uuid/v4";

const REQUEST_FETCH_TICKETS = "fetchTickets";
const REQUEST_FETCH_TICKET_ID = "fetchTicketId";
const REQUEST_CREATE_TICKET = "createTicket";
const REQUEST_UPDATE_TICKET = "updateTicket";

/**
 * Fetch support tickets list
 */
export function fetchTickets(sources) {
  const userId$ = sources.STATE.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_TICKETS
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => {
      const pageLimit = (action.payload && action.payload.pageLimit) || 50;
      const pageOffset = (action.payload && action.payload.pageOffset) || 0;

      const payload = {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          pageLimit: pageLimit,
          pageOffset: pageOffset,
          sortField: "created",
          sortOrder: "desc",
          ...(action.payload?.creatorId && {
            creatorId: action.payload.creatorId,
          }),
          ...(action.payload?.supportTicketStateId && {
            supportTicketStateId: action.payload.supportTicketStateId,
          }),
          ...(action.payload?.supportTicketPriorityId && {
            supportTicketPriorityId: action.payload.supportTicketPriorityId,
          }),
        },
      };

      return {
        url: NEW_BASE_URL + "/SupportTicket/list",
        category: REQUEST_FETCH_TICKETS,
        method: "POST",
        headers: generalHeader(null),
        withCredentials: true,
        send: payload,
      };
    });

  let httpResponse$ = sources.HTTP.select(REQUEST_FETCH_TICKETS)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.storeTickets(response.body)
        : actions.fetchTicketsFailed(response.body)
    );

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

/**
 * Fetch single support ticket by ID
 */
export function fetchTicketId(sources) {
  const userId$ = sources.STATE.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_TICKET_ID
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => {
      const ticketId = action.payload?.id;

      const payload = {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          id: ticketId,
        },
      };

      return {
        url: NEW_BASE_URL + "/SupportTicket/get",
        category: REQUEST_FETCH_TICKET_ID,
        method: "POST",
        headers: generalHeader(null),
        withCredentials: true,
        send: payload,
      };
    });

  let httpResponse$ = sources.HTTP.select(REQUEST_FETCH_TICKET_ID)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.storeTicketId(response.body)
        : actions.fetchTicketIdFailed(response.body)
    );

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

/**
 * Create new support ticket
 */
export function createTicket(sources) {
  const csrfToken$ = sources.STATE.map((state) => state.applicationUser?.token);
  const userId$ = sources.STATE.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.CREATE_TICKET
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => {
      const {
        title,
        shortDescription,
        supportTicketStateId,
        supportTicketPriorityId,
        initialMessage,
      } = action.payload;

      const payload = {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          title: title,
          shortDescription: shortDescription || "",
          supportTicketStateId: supportTicketStateId,
          supportTicketPriorityId: supportTicketPriorityId,
          initialMessage: initialMessage || "",
        },
      };

      return {
        url: NEW_BASE_URL + "/SupportTicket/create",
        category: REQUEST_CREATE_TICKET,
        method: "POST",
        headers: generalHeader(csrfToken),
        withCredentials: true,
        send: payload,
      };
    });

  let httpResponse$ = sources.HTTP.select(REQUEST_CREATE_TICKET)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) => {
      // API returns 200, check for exception field in response body
      if (response.body && response.body.exception) {
        return actions.createTicketFailed(response.body);
      } else {
        return actions.createTicketSucceed(response.body);
      }
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

/**
 * Update existing support ticket
 */
export function updateTicket(sources) {
  const csrfToken$ = sources.STATE.map((state) => state.applicationUser?.token);
  const userId$ = sources.STATE.map((state) => state.applicationUser?.uid);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.UPDATE_TICKET
  )
    .compose(sampleCombine(csrfToken$, userId$))
    .map(([action, csrfToken, userId]) => {
      const {
        id,
        title,
        shortDescription,
        supportTicketStateId,
        supportTicketPriorityId,
      } = action.payload;

      const payload = {
        header: {
          id: v4(),
          application: v4(),
          bank: "NBG",
          userId: userId || v4(),
        },
        payload: {
          id: id,
          ...(title && { title }),
          ...(shortDescription && { shortDescription }),
          ...(supportTicketStateId && { supportTicketStateId }),
          ...(supportTicketPriorityId && { supportTicketPriorityId }),
        },
      };

      return {
        url: NEW_BASE_URL + "/SupportTicket/update",
        category: REQUEST_UPDATE_TICKET,
        method: "POST",
        headers: generalHeader(csrfToken),
        withCredentials: true,
        send: payload,
      };
    });

  let httpResponse$ = sources.HTTP.select(REQUEST_UPDATE_TICKET)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) => {
      if (response.body && response.body.exception) {
        return actions.updateTicketFailed(response.body);
      } else {
        return actions.updateTicketSucceed(response.body);
      }
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}
